
import Vue, { PropType } from 'vue';
import ProductPreview from './components/ProductPreview.vue';
import ProductPreviewModal from './components/ProductPreviewModal.vue';
import { mapState, mapActions } from 'pinia';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { CvButton } from '@carbon/vue';
import Save16 from '@carbon/icons-vue/es/save/16';
import { PdpChoicesSetterType } from '@/setters/typings';
import { Personalization } from '@midocean/webapps-shared-domain';
import { trySavePersonalization } from '@/utils/personalizationHelperV2';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { SwitchColor } from '../SwitchColor';
import GeneratePdf16 from '@carbon/icons-vue/es/generate-pdf/16';

declare module 'vue/types/vue' {
  interface Vue {
    $pdpChoices: PdpChoicesSetterType;
    $sapCustomerId: string;
    $isEditMode: boolean;
    $personalization: Personalization;
    $isDirectApproval: boolean;
    $isConceptDisabled: boolean;
    $isOrderDisabled: boolean;
    $customerCompanyLogoUrl: string;
  }
}

export default Vue.extend({
  name: 'ProductManagement',

  inject: [
    '$pdpChoices',
    '$sapCustomerId',
    '$isEditMode',
    '$personalization',
    '$isDirectApproval',
    '$isConceptDisabled',
    '$isOrderDisabled',
    '$customerCompanyLogoUrl',
  ],

  components: {
    ProductPreview,
    ProductPreviewModal,
    SwitchColor,
    CvButton,
    GeneratePdf16,
  },

  data() {
    return {
      isPreviewModalOpen: false,
      Save16: Save16,
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'selectedPrintPosition',
      'unavailablePrintPositions',
    ]),
    ...mapState(useShapeStore, ['shapes']),
    ...mapState(useGlobalStore, ['isProcessingOrder', 'isLoaderActive']),
    ...mapState(useLangStore, ['languageInUse']),

    isPreviewDisabled(): boolean {
      return (
        this.unavailablePrintPositions.length === 0 || this.shapes.length === 0
      );
    },
  },

  methods: {
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),
    handleSaveConcept() {
      const triggerSmallDetailsCheck =
        this.shapes.filter(
          (x) =>
            x.smallDetailsResult &&
            (x.smallDetailsResult.status === 'Average' ||
              x.smallDetailsResult.status === 'Bad' ||
              !x.smallDetailsResult.isCheckValid)
        ).length > 0;

      if (DoSmallDetailsPart && triggerSmallDetailsCheck) {
        this.$emit('action:trigger-small-details');
      } else {
        this.setIsProcessingOrder(true);
        this.setIsLoaderActive(true);
        try {
          const personalizationId = trySavePersonalization(
            this.$isEditMode,
            this.$pdpChoices.productVariants,
            this.$sapCustomerId,
            true,
            this.$isDirectApproval,
            this.$customerCompanyLogoUrl,
            this.languageInUse
          );

          this.$emit('wizard:ordered', { personalizationId });
          this.$emit('action:disable-pintura');
        } catch (e: unknown) {
          console.log((e as Error).message);
        } finally {
          this.setIsProcessingOrder(false);
          this.setIsLoaderActive(false);
        }
      }
    },
  },
});
